import { gql } from '@apollo/client';
import {
  useLazyQuery,
  useLocalStorage,
  useMutation,
  useMyUrl,
  useTranslation,
} from '@gimlite/common';
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Color,
  Description,
  DurationPicker,
  Empty,
  Form,
  Icon,
  IconType,
  Image,
  Page,
  Popover,
  Row,
  Space,
  Table,
  useStoreUIV1,
  Widget,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import dayjs from 'dayjs';
import { DateTime, Interval } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';
import {
  Mutation,
  MutationUpdateSessionArgs,
  ParkingRightMin,
  Query,
  QuerySessionArgs,
  QuerySessionsArgs,
  TerminalStateEntity,
  UpsEntity,
} from '../../../../client/graphql';
import {
  formattedDuration,
  formattedDurationMinutes,
} from '../../../common/duration';
import {
  Cover,
  coverAuthorizedType,
} from '../../../components/cover/cover.component';

import { GET_SESSION } from '../gql/session.gql';
import { GET_SESSIONS } from '../gql/vao.sessions.gql';
import './vao.page.scss';

const registeryIconLeft: Record<string, IconType.Config.Type> = {
  MOBILE: 'faMobileScreenSolid',
  FINE: 'customAgent',
  SUBSCRIPTION: 'faSquareParkingSolid',
  SHORT_TERM: 'faSquareParkingSolid',
  OFFSTREET: 'faSquareParkingSolid',
};

export const Vao = observer(() => {
  const { SSOStore, WizardStore } = useStoreUIV1();
  const me = SSOStore.user! as Query['me'];
  const { getParamsUrl, setParamsUrl, removeParamsUrl } = useMyUrl<{
    minUncoveredDuration: number;
    ignoreTerminalState: boolean;
  }>();
  const { t, lang }: any = useTranslation();
  const { id } = useParams();
  const live = useRef<boolean>(false);
  const [liveRender, setLiveRender] = useState<boolean>(false);
  const [minUncoveredDuration, setMinUncoveredDuration] = useLocalStorage(
    'minUncoveredDuration',
    undefined,
  );

  const [sessionsCall, sessionsState] = useLazyQuery<
    { sessions: Query['sessions'] },
    QuerySessionsArgs
  >(GET_SESSIONS, {
    socket: {
      'sessions:created': (data) => {
        console.log('data in sessions:updated', data);
        return (
          live.current && data?.session?.upsId && data?.session?.upsId === id
        );
      },
      'sessions:updated': (data) => {
        console.log('data in sessions:updated', data);
        return (
          'reason' in data &&
          data?.reason === 'FPS_CREATED' &&
          data?.upsId === id
        );
      },
    },
  });

  const [sessionCall, sessionState] = useLazyQuery<
    { session: Query['session'] },
    QuerySessionArgs
  >(GET_SESSION, {
    socket: {
      'sessions:updated': (data: any, currentData): boolean => {
        if (
          data &&
          currentData &&
          data?.sessionId === currentData?.session?._id &&
          data?.reason === 'FPS_CREATED' &&
          data?.upsId === id
        ) {
          sessionState.resetData();
        }

        return false;
      },
    },
  });

  const [updateSessionCall, updateSessionState] = useMutation<
    { updateSession: Mutation['updateSession'] },
    MutationUpdateSessionArgs
  >(gql`
    mutation updateSession(
      $sessionId: String!
      $input: UpdateControlSessionsDto!
    ) {
      updateSession(sessionId: $sessionId, input: $input) {
        _id
      }
    }
  `);

  const sessions = useMemo(() => {
    if (!sessionsState.data) return undefined;

    const { list, paging } = sessionsState.data?.sessions;

    return {
      list: list.map(
        ({ _id, previousClaimsForUser, endDate, uncoveredDuration }) => {
          return {
            _id,
            previousClaimsForUser,
            endDate: endDate
              ? DateTime.fromISO(endDate).toFormat('dd/MM/yyyy HH:mm:ss')
              : null,
            uncoveredDuration: {
              text: uncoveredDuration
                ? formattedDuration(uncoveredDuration * 60 * 1000)
                : null,
              icon: 'faClockRotateLeftSolid',
              time: uncoveredDuration,
            },
          };
        },
      ),
      paging,
    };
  }, [sessionsState.data]);

  const session = useMemo(() => {
    if (!sessionState.data) return undefined;

    const {
      _id,
      lpn,
      covers,
      endDate,
      startDate,
      uncoveredDuration,
      rightsDuration,
      terminalState,
      entryEvent,
      exitEvent,
      controlCtx,
      ups,

      ...rest
    } = sessionState.data?.session;

    const cameraEntry = entryEvent?.resources?.list?.map(({ url }) => url);
    const cameraExit = exitEvent?.resources?.list?.map(({ url }) => url);

    const camera = {
      entry:
        cameraEntry && cameraEntry?.length > 0 ? cameraEntry[0] : undefined,
      exit: cameraExit && cameraExit?.length > 0 ? cameraExit[0] : undefined,
    };

    return {
      _id,
      sessionStartDate: startDate,
      sessionEndDate: endDate,
      forCover: {
        covers: Array.from(covers)
          .filter((item) => coverAuthorizedType.includes(item.coverType))
          .sort((a, b) => {
            const startDateA = new Date(a.startDate).getTime();
            const startDateB = new Date(b.startDate).getTime();
            return startDateA > startDateB ? 1 : -1;
          }),
        session: {
          startDate,
          endDate,
        },
      },
      lpn,
      forDes: {
        entry: {
          textItem: DateTime.fromISO(startDate).toFormat('dd/MM/yy HH:mm:ss'),
        },
        totalDuration: {
          textItem: endDate
            ? formattedDuration(
                DateTime.fromISO(endDate)
                  .diff(DateTime.fromISO(startDate))
                  .toMillis(),
              )
            : null,
        },
        rightDuration: {
          textItem:
            rightsDuration && rightsDuration > 0
              ? formattedDurationMinutes(rightsDuration)
              : '00:00',
        },
        defaultRight: {
          textItem: uncoveredDuration
            ? formattedDuration(uncoveredDuration * 60 * 1000)
            : null,
          iconColor: uncoveredDuration,
        },
        horodator: terminalState,
        exit: {
          textItem: endDate
            ? DateTime.fromISO(endDate).toFormat('dd/MM/yy HH:mm:ss')
            : null,
        },
      },
      camera,
      date: {
        start: startDate,
        end: endDate,
      },
      history: controlCtx?.ctxRelevantCovers || [],
      fps: {
        sessionId: _id,
        upsId: ups._id,
        terminalId: ups.config?.p1000?.pointOfSaleId,
        parkId: ups.config?.p1000?.legacyId,
        statementDatetime: endDate,
        confirm: {
          right: !!covers.find(({ coverType }) => coverType === 'SHORT_TERM'),
          claim: controlCtx?.ctxRelevantCovers
            ? controlCtx?.ctxRelevantCovers?.map(
                ({ claimReason }) => claimReason,
              ).length > 0
            : false,
        },
        claimReasons: controlCtx?.ctxRelevantCovers?.map(
          ({ claimReason }) => claimReason,
        ),
        address: ups?.address,
        maximumAmount: ups?.config?.fps?.maximumAmount,
        entry: {
          camera: camera.entry,
          date: startDate,
        },
        exit: {
          camera: camera.exit,
          date: endDate,
        },
        lpn,
      },
      ...rest,
    };
  }, [sessionState.data]);

  const updateSession = useMemo(() => {
    if (!updateSessionState.data) return undefined;

    return updateSessionState.data?.updateSession;
  }, [updateSessionState.data]);

  const upsConfig = me?.data?.ups?.reduce(
    (acc: Record<string, Omit<UpsEntity, '_id'>>, { _id, ...rest }) => ({
      ...acc,
      [_id]: { ...rest },
    }),
    {},
  );

  const isVerbalizationAllowed = me?.data?.canVerbalize!!;

  const historyIconLeft = useCallback(
    ({
      tariffType,
      usedMeansOfPayment,
    }: Pick<
      ParkingRightMin,
      'tariffType' | 'usedMeansOfPayment'
    >): IconType.Config.Type => {
      let tariffTypeFormatted = tariffType;

      if (tariffType === 'SHORT_TERM' && usedMeansOfPayment === 'MOBILE') {
        tariffTypeFormatted = 'MOBILE';
      }

      return registeryIconLeft[tariffTypeFormatted];
    },
    [],
  );

  const historyIconRight = useCallback(
    ({
      tariffType,
      startDate,
      endDate,
      upsId,
      currentUpsId,
      sessionStartDate,
      sessionEndDate,
      status,
      claimReason,
      cancellationReason,
    }: Pick<
      ParkingRightMin,
      | 'tariffType'
      | 'startDate'
      | 'endDate'
      | 'upsId'
      | 'status'
      | 'claimReason'
      | 'cancellationReason'
    > & {
      currentUpsId: string;
      sessionStartDate: Date | null;
      sessionEndDate: Date | null;
    }): {
      icon: IconType.Config.Type;
      color: Extract<
        Color,
        'error' | 'primary' | 'default' | 'warn' | 'disabled'
      >;
      tooltip?: string;
    } => {
      const isInvalid =
        upsId !== currentUpsId ||
        !sessionEndDate ||
        !sessionStartDate ||
        !startDate ||
        !endDate ||
        !(
          new Date(startDate) <= new Date(sessionEndDate) &&
          new Date(endDate) >= new Date(sessionStartDate)
        );

      if (tariffType === 'SUBSCRIPTION') {
        return {
          icon: 'faCalendarDaysSolid',
          color: isInvalid ? 'error' : 'primary',
        };
      }

      if (tariffType === 'OFFSTREET') {
        return { icon: 'faSquareParkingSolid', color: 'primary' };
      }

      if (tariffType === 'SHORT_TERM') {
        return {
          icon: 'faClockRegular',
          color: isInvalid ? 'error' : 'default',
        };
      }

      if (tariffType === 'FINE') {
        if (status === 'CANCELLED') {
          return {
            icon: 'faCircleXmarkSolid',
            color: 'error',
            tooltip: t(cancellationReason),
          };
        } else if (status === 'CORRECTION' || !!claimReason) {
          return { icon: 'faCarOnSolid', color: 'warn' };
          //* INITIAL
        } else {
          return {
            icon: 'faClockRegular',
            color: 'primary',
            tooltip: t('CLAIM_REASON_' + claimReason),
          };
        }
      }

      return { icon: 'faCircleQuestionSolid', color: 'disabled' };
    },
    [],
  );

  const historyFormatted = useCallback(
    (history: ParkingRightMin[]) => {
      if (!id) return [];

      return history
        ?.slice()
        ?.sort((a, b) => {
          const dateA = a?.startDate ? new Date(a.startDate).getTime() : 0;
          const dateB = b?.startDate ? new Date(b.startDate).getTime() : 0;
          return dateB - dateA;
        })
        .map(
          ({
            tariffType,
            startDate,
            endDate,
            usedMeansOfPayment,
            upsId,
            legacyId,
            status = null,
            cancellationReason,
            claimReason,
          }) => {
            const _id = uuidV4();
            const iconLeft = historyIconLeft({
              tariffType,
              usedMeansOfPayment,
            });
            const date = dayjs(startDate).format('DD/MM/YY HH:mm');
            const upsTranslationKey = `ups-${upsId}-name`;
            console.log('HISTORY AD', { legacyId, upsTranslationKey });
            const upsName =
              upsId &&
              upsTranslationKey.trim() !==
                t(upsTranslationKey).replaceAll('#', '').trim()
                ? t(upsTranslationKey)
                : '';
            const upsCode = legacyId?.[0];
            const iconRight = historyIconRight({
              tariffType,
              startDate: new Date(startDate).toISOString(),
              endDate: new Date(endDate).toISOString(),
              upsId,
              currentUpsId: id,
              sessionStartDate: session?.sessionStartDate
                ? new Date(session?.sessionStartDate)
                : null,
              sessionEndDate: session?.sessionEndDate
                ? new Date(session?.sessionEndDate)
                : null,
              status,
              claimReason,
              cancellationReason,
            });

            const durationCalc = formattedDuration(
              new Date(endDate).getTime() - new Date(startDate).getTime(),
            ).replace('[d]', t('DAY_FIRST_LETTER'));

            return {
              _id,
              pointOfSale: (
                <Icon
                  config={{ type: iconLeft, color: 'primary', size: 'large' }}
                ></Icon>
              ),
              ups: (
                <Col config={{ vertical: 'center' }}>
                  <Write
                    data={{
                      item: `${upsCode}${upsCode && upsName ? ' -' : ''} ${upsName}`.trim(),
                    }}
                    config={{
                      mode: 'namise',
                    }}
                  ></Write>
                  <Write
                    data={{ item: date }}
                    config={{
                      mode: 'namise-description',
                    }}
                  ></Write>
                </Col>
              ),
              duration: (
                <div className="historyDuration">
                  {cancellationReason && (
                    <Popover
                      config={{ trigger: 'hover', placement: 'bottom' }}
                      data={<Write data={{ item: cancellationReason }} />}
                    >
                      <Icon
                        className="historyDurationIcon"
                        config={{
                          type: iconRight?.icon,
                          color: iconRight?.color,
                          size: 'xsmall',
                        }}
                      />
                    </Popover>
                  )}

                  {!cancellationReason && claimReason && (
                    <Popover
                      config={{ trigger: 'hover', placement: 'bottom' }}
                      data={<Write data={{ item: t(claimReason) }} />}
                    >
                      <Icon
                        className="historyDurationIcon"
                        config={{
                          type: iconRight?.icon,
                          color: iconRight?.color,
                          size: 'xsmall',
                        }}
                      />
                    </Popover>
                  )}

                  {!cancellationReason && !claimReason && (
                    <Icon
                      className="historyDurationIcon"
                      config={{
                        type: iconRight?.icon,
                        color: iconRight?.color,
                        size: 'xsmall',
                      }}
                    />
                  )}

                  <Write
                    data={{ item: durationCalc }}
                    config={{ mode: 'value-medium', wrap: false }}
                  ></Write>
                </div>
              ),
            };
          },
        );
    },
    [lang, id, session],
  );

  const intrepretUncoveredDurationColor = useCallback(
    (duration: number) => {
      const findUps = me?.data?.ups?.find((ups: any) => ups?._id === id);

      const outrageTreshold = findUps?.config?.prestoScan?.outrageTreshold;

      if (!(typeof duration === 'number') || !findUps || !outrageTreshold)
        return 'basic';

      const { __typename, ...outrageTresholdClear } = outrageTreshold;

      const interpret: any = {
        danger: 'error',
        warning: 'warn',
        valid: 'success',
      };

      const formattedOutrageTreshold = Object.entries(outrageTresholdClear)
        .sort((a: any, b: any) => b[1] - a[1])
        .find(([key, value]: any) => duration >= value);
      if (!formattedOutrageTreshold) return 'basic';

      const [label] = formattedOutrageTreshold;
      return interpret?.[label] || 'basic';
    },
    [me, id],
  );

  const filterAlarms = useCallback(
    (alarms: TerminalStateEntity[]) => {
      const sessionInterval = Interval.fromDateTimes(
        DateTime.fromISO(session!.sessionStartDate!),
        DateTime.fromISO(session!.sessionEndDate!),
      );
      return alarms.filter(({ startDate, endDate }) => {
        const alarmInterval = Interval.fromDateTimes(
          DateTime.fromISO(startDate!),
          DateTime.fromISO(endDate!),
        );
        return sessionInterval.overlaps(alarmInterval);
      });
    },
    [session],
  );

  const formattedTerminalState = useCallback(
    (alarms: TerminalStateEntity[]) => {
      return (
        <ul className="horodatorInfoList">
          {filterAlarms(alarms)
            .sort((a, b) => {
              const dateA = a?.startDate ? new Date(a.startDate).getTime() : 0;
              const dateB = b?.startDate ? new Date(b.startDate).getTime() : 0;
              return dateB - dateA;
            })
            .sort((a, b) => {
              const dateA = a?.startDate ? new Date(a.startDate).getTime() : 0;
              const dateB = b?.startDate ? new Date(b.startDate).getTime() : 0;
              return dateB - dateA;
            })
            .map(({ startDate, endDate, terminalId, name }, index) => {
              const translatedText = t('HORODATOR_LINE_ALARM')
                .replace(/[\<\>]/g, '')
                .replace(
                  '[startDate]',
                  `${
                    startDate
                      ? dayjs(startDate).format('DD/MM/YY HH:mm')
                      : '***'
                  }`,
                )
                .replace(
                  '[endDate]',
                  `${endDate ? dayjs(endDate).format('DD/MM/YY HH:mm') : '***'}`,
                )
                .replace('[terminalId]', `${terminalId}`)
                .replace('[description]', `<strong>${name}</strong>`);

              return (
                <li
                  key={`horodator-info-item-${index}`}
                  dangerouslySetInnerHTML={{
                    __html: translatedText,
                  }}
                ></li>
              );
            })}
        </ul>
      );
    },
    [lang, id, session],
  );

  const loadSessions = useCallback(
    ({
      upsId,
      getParamsUrl,
    }: {
      getParamsUrl: Partial<{
        minUncoveredDuration: number;
        ignoreTerminalState: boolean;
      }>;
      upsId?: string;
    }) => {
      if (upsId) {
        const searchRequest = {
          limit: 50,
          ...getParamsUrl,
          upsId,
        };

        if (
          'minUncoveredDuration' in searchRequest &&
          !searchRequest?.minUncoveredDuration
        ) {
          delete searchRequest.minUncoveredDuration;
        }

        if (searchRequest.minUncoveredDuration) {
          searchRequest.minUncoveredDuration = Math.floor(
            searchRequest.minUncoveredDuration / (1000 * 60),
          );
        }

        sessionsCall({ variables: { ...(searchRequest as any) } });
      }
    },
    [],
  );

  const handleSessionTime = useCallback(
    (text: string) => text.replace('[d]', t('DAY_FIRST_LETTER')),
    [lang],
  );

  useEffect(() => {
    loadSessions({ upsId: id, getParamsUrl });

    return () => {
      sessionState.resetData();
    };
  }, [getParamsUrl, id, me, updateSession]);

  return (
    <Page className="vao">
      <Zone
        config={{
          gap: { x: 2 },
          zones: [['vao', 'infraction']],
          rows: ['1fr'],
          columns: ['minmax(400px, 18vw)', '1fr'],
        }}
      >
        <Zone.Area config={{ area: 'vao' }}>
          <Widget.Group
            state={{
              error: sessionsState.error,
              loading: sessionsState.loading,
              refetch: () => sessionsState.refetch?.({ page: 1 }),
              live: liveRender,
            }}
            config={{
              icon: 'faCameraSolid',
              title: `${t('vao')} (${sessions?.paging?.count || 0} ${t(
                sessions?.paging?.count && sessions?.paging?.count > 1
                  ? 'sessions'
                  : 'session',
              ).toLowerCase()})`,
            }}
          >
            <Zone
              config={{
                zones: [['list']],
                rows: ['1fr'],
                columns: ['1fr'],
              }}
            >
              <Zone.Area config={{ area: 'list' }}>
                <Widget>
                  <Zone
                    config={{
                      gap: {
                        y: 2,
                      },
                      zones: [['filter'], ['table']],
                      rows: ['min-content', '1fr'],
                      columns: ['1fr'],
                    }}
                  >
                    <Zone.Area config={{ area: 'filter' }}>
                      <Widget>
                        <Form
                          data={{
                            value: {
                              ...getParamsUrl,
                              ignoreTerminalState:
                                !!getParamsUrl?.ignoreTerminalState,
                            },
                          }}
                          handleEvent={{
                            change: (data: any) => {
                              if (!data?.minUncoveredDuration) {
                                removeParamsUrl('minUncoveredDuration');
                                delete data?.minUncoveredDuration;
                                delete getParamsUrl?.minUncoveredDuration;
                                setMinUncoveredDuration(undefined);
                              } else {
                                setMinUncoveredDuration(
                                  data?.minUncoveredDuration,
                                );
                              }

                              setParamsUrl({
                                ...getParamsUrl,
                                page: 1,
                                ...data,
                              });

                              sessionState.resetData();
                            },
                          }}
                        >
                          <Row
                            config={{
                              horizontal: 'spaceBetween',
                              vertical: 'end',
                            }}
                          >
                            <Form.Item
                              config={{
                                name: 'minUncoveredDuration',
                                label: t('durationOfDefaultOfRights'),
                                way: 'vertical',
                                rules: [],
                              }}
                            >
                              <DurationPicker
                                config={{
                                  format: 'HH:mm',
                                  width: 'full',
                                  clear: true,
                                  presets: [
                                    {
                                      label: `30 ${t('minutes').toLowerCase()}`,
                                      value: 1000 * 60 * 30,
                                    },
                                    {
                                      label: `1 ${t('hour').toLowerCase()}`,
                                      value: 1000 * 60 * 60,
                                    },
                                    {
                                      label: `2 ${t('hours').toLowerCase()}`,
                                      value: 1000 * 60 * 60 * 2,
                                    },
                                    {
                                      label: `4 ${t('hours').toLowerCase()}`,
                                      value: 1000 * 60 * 60 * 4,
                                    },
                                  ],
                                }}
                              />
                            </Form.Item>
                          </Row>
                          <Space />
                          <Row
                            config={{
                              horizontal: 'start',
                              vertical: 'center',
                            }}
                          >
                            <Form.Item
                              config={{
                                name: 'ignoreTerminalState',
                                label: t('ignoreTerminalState'),
                                way: 'horizontal',
                                rules: [],
                                tooltip: t('ignoreTerminalStateTooltip'),
                              }}
                            >
                              <Checkbox />
                            </Form.Item>
                          </Row>
                        </Form>
                      </Widget>
                    </Zone.Area>

                    <Zone.Area config={{ area: 'table' }}>
                      <Widget>
                        <Table<any>
                          handleEvent={{
                            read: async ({ _id }) => {
                              await sessionCall({ variables: { id: _id } });
                            },
                            live: (value) => {
                              live.current = value;
                              setLiveRender(value);
                              if (value) {
                                sessionsState.refetch?.({ page: 1 });
                              }
                            },
                            paging: async (value: any) => {
                              const searchRequest = {
                                upsId: id,
                                ...getParamsUrl,
                                ...value,
                                limit: 50,
                              };

                              if (
                                'minUncoveredDuration' in searchRequest &&
                                !searchRequest?.minUncoveredDuration
                              )
                                delete searchRequest.minUncoveredDuration;

                              if (searchRequest.minUncoveredDuration) {
                                searchRequest.minUncoveredDuration = Math.floor(
                                  searchRequest.minUncoveredDuration /
                                    (1000 * 60),
                                );
                              }

                              await sessionsCall({
                                variables: { ...searchRequest },
                              });
                            },
                          }}
                          data={
                            sessions
                              ? {
                                  list: sessions?.list?.map(
                                    ({
                                      uncoveredDuration: { text, icon, time },
                                      previousClaimsForUser,
                                      ...rest
                                    }: any) => ({
                                      ...rest,
                                      rapo: previousClaimsForUser ? (
                                        <div></div>
                                      ) : (
                                        ''
                                      ),
                                      uncoveredDuration: (
                                        <Badge
                                          config={{
                                            size: 'medium',
                                            text: handleSessionTime(text),
                                            icon,
                                            color:
                                              intrepretUncoveredDurationColor(
                                                time,
                                              ),
                                          }}
                                        ></Badge>
                                      ),
                                    }),
                                  ),
                                  paging: sessions.paging,
                                }
                              : undefined
                          }
                          config={{
                            alternateColor: true,
                            actions: { read: true },
                            pagination: 'scroll',
                            columns: [
                              {
                                title: '',
                                key: 'rapo',
                              },
                              {
                                title: t('endOfSession'),
                                key: 'endDate',
                              },
                              {
                                title: t('defaultOfRights'),
                                key: 'uncoveredDuration',
                              },
                            ],
                            testId: 'vao-table',
                          }}
                        />
                      </Widget>
                    </Zone.Area>
                  </Zone>
                </Widget>
              </Zone.Area>
            </Zone>
          </Widget.Group>
        </Zone.Area>

        <Zone.Area config={{ area: 'infraction' }}>
          {session ? (
            <Widget.Group
              state={{
                error: sessionState.error,
                loading: sessionState.loading,
                refetch: sessionState.refetch,
                live: true,
              }}
              config={{
                title: t('infringementReport'),
                extra: (
                  <>
                    {session?.lpn && (
                      <div className="descriptionItem vaoPlate">
                        <Icon
                          className="descriptionItemIcon"
                          config={{
                            type: 'faCarSolid',
                            color: 'primary',
                            size: 'small',
                          }}
                        ></Icon>

                        <div className="vaoPlateDetails">
                          <Write
                            data={{ item: session.lpn }}
                            config={{
                              mode: 'link',
                              color: 'primary',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ),
              }}
            >
              <div className="vaoVerbalise">
                <Button
                  handleEvent={{
                    click: () => {
                      WizardStore.select({
                        id: 'createFps',
                        store: { fps: session.fps },
                      });
                    },
                  }}
                  config={{
                    disabled: !isVerbalizationAllowed,
                    color: 'error',
                    weight: 'bold',
                    text: t('IVerbalize'),
                    size: 'large',
                    testId: 'createFpsBtn',
                  }}
                />
                <Space config={{ way: 'horizontal' }} />
                <Button
                  handleEvent={{
                    click: async () => {
                      await updateSessionCall({
                        variables: {
                          sessionId: session._id,
                          input: {
                            controlState: 'NOT_FINED',
                          },
                        },
                      });

                      sessionState.resetData();
                    },
                  }}
                  config={{
                    disabled: !isVerbalizationAllowed,
                    size: 'large',
                    mode: 'stroke',
                    weight: 'bold',
                    text: t('IDontVerbalize'),
                    color: 'primary',
                  }}
                />
              </div>

              <Zone
                config={{
                  zones: [
                    ['description', 'description'],
                    ['cameraEnter', 'cameraExit'],
                    ['more', 'more'],
                  ],
                  rows: ['min-content', '1fr', 'min-content'],
                  columns: ['1fr', '1fr'],
                }}
              >
                <Zone.Area config={{ area: 'description' }}>
                  <Widget>
                    <Space config={{ count: 1, way: 'vertical' }} />
                    {session?.forDes && (
                      <Description
                        config={{ way: 'vertical' }}
                        data={[
                          {
                            key: 'entry',
                            label: t('entry'),
                            value: (
                              <Write
                                data={{
                                  item: session?.forDes?.entry?.textItem,
                                }}
                                config={{
                                  mode: 'value-large',
                                }}
                              />
                            ),
                          },
                          {
                            key: 'totalDuration',
                            label: t('totalDuration'),
                            value: (
                              <div className="descriptionItem">
                                <Icon
                                  className="descriptionItemIcon"
                                  config={{
                                    type: 'faClockRegular',
                                    color: 'disabled',
                                    size: 'xsmall',
                                  }}
                                ></Icon>
                                <Write
                                  data={{
                                    item:
                                      session?.forDes?.totalDuration?.textItem?.replace(
                                        '[d]',
                                        t('DAY_FIRST_LETTER'),
                                      ) || null,
                                  }}
                                  config={{
                                    mode: 'value-large',
                                  }}
                                />
                              </div>
                            ),
                          },
                          {
                            key: 'ofWhichRight',
                            label: t('ofWhichRight'),
                            value: (
                              <div className="descriptionItem">
                                <Icon
                                  className="descriptionItemIcon"
                                  config={{
                                    type: 'faSquareCheckSolid',
                                    color: 'success',
                                    size: 'xsmall',
                                  }}
                                ></Icon>
                                <Write
                                  data={{
                                    item: session?.forDes?.rightDuration?.textItem.replace(
                                      '[d]',
                                      t('DAY_FIRST_LETTER'),
                                    ),
                                  }}
                                  config={{
                                    mode: 'value-large',
                                  }}
                                />
                              </div>
                            ),
                          },
                          {
                            key: 'defaultOfRights',
                            label: t('defaultOfRights'),
                            value: (
                              <div className="descriptionItem">
                                <Icon
                                  className="descriptionItemIcon"
                                  config={{
                                    type: 'faClockRotateLeftSolid',
                                    size: 'xsmall',
                                    color: session?.forDes?.defaultRight
                                      ?.iconColor
                                      ? intrepretUncoveredDurationColor(
                                          session?.forDes?.defaultRight
                                            ?.iconColor,
                                        )
                                      : undefined,
                                  }}
                                ></Icon>
                                <Write
                                  data={{
                                    item:
                                      session?.forDes?.defaultRight?.textItem?.replace(
                                        '[d]',
                                        t('DAY_FIRST_LETTER'),
                                      ) || null,
                                  }}
                                  config={{
                                    mode: 'value-large',
                                  }}
                                />
                              </div>
                            ),
                          },
                          {
                            key: 'parkingMeter',
                            label: t('parkingMeter'),
                            value:
                              session?.forDes?.horodator &&
                              session?.forDes?.horodator?.length > 0 &&
                              filterAlarms(session?.forDes?.horodator)?.length >
                                0 ? (
                                <Popover
                                  config={{
                                    trigger: 'hover',
                                    placement: 'bottom',
                                    maxHeight: 'large',
                                  }}
                                  data={
                                    <div className="horodatorInfo">
                                      <span className="horodatorInfoTitle">
                                        {t('HORODATOR_TITLE_ALARM')}
                                      </span>
                                      {formattedTerminalState(
                                        session?.forDes?.horodator,
                                      )}
                                    </div>
                                  }
                                >
                                  <div className="descriptionItem">
                                    <Icon
                                      className="descriptionItemIcon"
                                      config={{
                                        type: 'faTriangleExclamationSolid',
                                        color: 'warn',
                                        size: 'small',
                                      }}
                                    ></Icon>
                                    <Write
                                      data={{
                                        item: t('problem'),
                                      }}
                                      config={{
                                        mode: 'value-large',
                                      }}
                                    />
                                  </div>
                                </Popover>
                              ) : (
                                <div className="descriptionItem">
                                  <Icon
                                    className="descriptionItemIcon"
                                    config={{
                                      type: 'faHeartPulseSolid',
                                      color: 'success',
                                      size: 'xsmall',
                                    }}
                                  ></Icon>
                                  <Write
                                    data={{
                                      item: 'OK',
                                    }}
                                    config={{
                                      mode: 'value-large',
                                    }}
                                  />
                                </div>
                              ),
                          },
                          {
                            key: 'exit',
                            label: t('exit'),
                            value: (
                              <Write
                                data={{
                                  item: session?.forDes?.exit?.textItem,
                                }}
                                config={{
                                  mode: 'value-large',
                                }}
                              />
                            ),
                          },
                        ]}
                      />
                    )}
                  </Widget>
                </Zone.Area>

                <Zone.Area config={{ area: 'cameraEnter' }}>
                  <Widget>
                    <Image
                      data={{
                        src: session?.camera?.entry,
                      }}
                      config={{
                        size: 'full',
                        preview: true,
                      }}
                    />
                    <Badge
                      className="vaoBadge"
                      config={{
                        size: 'xlarge',
                        radius: false,
                        text: t(`entry`),
                        icon: 'faArrowRightToBracketSolid',
                        color: 'success',
                      }}
                    ></Badge>
                  </Widget>
                </Zone.Area>

                <Zone.Area config={{ area: 'cameraExit' }}>
                  <Widget>
                    <Image
                      data={{
                        src: session?.camera?.exit,
                      }}
                      config={{
                        size: 'full',
                        preview: true,
                      }}
                    />
                    <Badge
                      className="vaoBadge"
                      config={{
                        size: 'xlarge',
                        radius: false,
                        text: t(`exit`),
                        icon: 'faArrowRightFromBracketSolid',
                        color: 'warn',
                      }}
                    ></Badge>
                  </Widget>
                </Zone.Area>

                <Zone.Area config={{ area: 'more' }}>
                  <Widget>
                    <Zone
                      config={{
                        zones: [['cover', 'history']],
                        rows: [`1fr`],
                        columns: ['2fr', '1fr'],
                      }}
                    >
                      <Zone.Area config={{ area: 'cover' }}>
                        <Widget
                          config={{
                            title: `${t('coverage')} (${session?.forCover.covers?.length || 0})`,
                          }}
                        >
                          <Cover data={session?.forCover} />
                        </Widget>
                      </Zone.Area>

                      <Zone.Area config={{ area: 'history' }}>
                        {/* <Space config={{ way: 'vertical' }} /> */}

                        <Widget
                          config={{
                            title: `${t('history')} (${
                              session?.history?.length || 0
                            })`,
                          }}
                        >
                          <Table<any>
                            className="vaoHistory"
                            handleEvent={{
                              paging: async (value) => {
                                if (!id) return;

                                await sessionsCall({
                                  variables: {
                                    upsId: id,
                                    ...getParamsUrl,
                                    ...value,
                                  },
                                });
                              },
                            }}
                            data={{
                              list: historyFormatted(session?.history),
                              paging: { limit: 1, current: 1, count: 1 },
                            }}
                            config={{
                              alternateColor: true,
                              pagination: 'scroll',
                              columns: [
                                {
                                  title: '',
                                  key: 'pointOfSale',
                                },
                                {
                                  title: `${t('parking')} / ${t('date')}`,
                                  key: 'ups',
                                },
                                {
                                  title: `${t('duration')}`,
                                  key: 'duration',
                                },
                              ],
                            }}
                          />
                        </Widget>
                      </Zone.Area>
                    </Zone>
                  </Widget>
                </Zone.Area>
              </Zone>
            </Widget.Group>
          ) : (
            <Widget.Group
              config={{
                title: t('infringementReport'),
              }}
            >
              <div className="vaoNotSelectedSession">
                <Empty
                  config={{
                    mode: { name: 'select', text: `${t('selectSession')} ...` },
                  }}
                />
              </div>
            </Widget.Group>
          )}
        </Zone.Area>
      </Zone>
    </Page>
  );
});
